'use client'

import { useAtomValue } from 'jotai'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  isStreamDebugEnabled,
  recordFrame,
  recordStreamPerformanceEvent,
  startSessionSampler,
} from '@/lib/debug/stream-stats'
import { decodeServerMessage, encodeAuthMessage, encodePong } from '@/lib/proto/codec'
import { toast } from '@/lib/toast'
import { serviceUtil } from '@/lib/utils/service-util'
import {
  RESTART_RECONNECT_DELAY_MS,
  RESTART_TIMEOUT_MS,
} from '@/service/runtime-health/health-policy'
import type { RestartResult, WebSocketHealth } from '@/service/runtime-health/types'
import { authRetryDelayMs } from '@/service/ws/auth-retry-backoff'
import {
  createHeatmapChannelCache,
  type GetCachedHeatmapColumn,
} from '@/service/ws/heatmap-channel-cache'
import {
  createIncrementalTickerCache,
  type GetCachedIncrementalTicker,
} from '@/service/ws/incremental-ticker-cache'
import { messageEventQueueLagMs, sourceAgeMs } from '@/service/ws/message-timing'
import {
  addScopedListener,
  collectScopedListeners,
  countScopedListeners,
  removeScopedListener,
  type ScopedListenerRegistry,
} from '@/service/ws/scoped-listener-registry'
import { encodeSetStreamInterval, useEffectiveStreamInterval } from '@/service/ws/stream-interval'
import {
  isStreamPerformanceSampled,
  reportStreamPerformanceSample,
} from '@/service/ws/stream-performance-telemetry'
import { isLiveOrderbookHeatmap } from '@/service/ws/stream-routing-contract'
import { createSubscriptionRefreshCoordinator } from '@/service/ws/subscription-refresh'
import {
  createLingeringChannels,
  createTickerChannelCache,
  type GetCachedTicker,
  shouldLingerTickerChannel,
} from '@/service/ws/ticker-channel-cache'
import {
  combinedWebSocketConnectionStatus,
  partitionChannelsByTransport,
  transportLaneForListener,
  type WebSocketTransportLane,
} from '@/service/ws/transport-routing'
import useBaseWebSocket from '@/service/ws/use-base-websocket'
import { authAtom } from '@/state/auth'
import { noteConnectionActivity } from '@/state/connection'
import { ClientMessage, MessageSubject, ServerMessage } from '@/types/generated/public_api'

// Types
type MessageListener = (msg: ServerMessage) => void
type ChannelKey = string

interface WebSocketContextType {
  isConnected: boolean
  connectionStatus: string
  connect: () => void
  disconnect: () => void
  /**
   * Close and reopen the socket, resolving once it has re-authenticated (or
   * timed out). Sole owner of the close-then-reopen sequence — callers must not
   * hand-roll disconnect()+connect(), or two restarts race each other's auth
   * and resubscribe ordering.
   */
  restart: () => Promise<RestartResult>
  /**
   * Snapshot of connection health for the runtime recovery system. Deliberately
   * a getter over a mutable ref rather than React state: these fields update on
   * every frame off the wire, and putting them in state would re-render every
   * consumer beneath the provider.
   */
  getHealth: () => WebSocketHealth
  getListenerCount: () => number
  /**
   * Canonical keys of every channel currently subscribed on this socket, for
   * the debug export — the count alone (getHealth) can't say which symbols or
   * streams a laggy tab has accumulated.
   */
  getActiveChannels: () => string[]
  subscribe: (channels: Partial<MessageSubject>[]) => void
  unsubscribe: (channels: Partial<MessageSubject>[]) => void
  /** Returns how many channels were actually put on the wire (0 = rate-limited away). */
  refreshSubscriptions: (channels: Partial<MessageSubject>[]) => number
  /**
   * The latest ticker for a symbol whose ticker channel is live on the wire, for a stream hook
   * to replay to a subscriber that joined an existing channel (see ticker-channel-cache.ts).
   */
  getCachedIncrementalTicker: GetCachedIncrementalTicker
  getCachedTicker: GetCachedTicker
  getCachedHeatmapColumn: GetCachedHeatmapColumn
  sendProto: (data: Uint8Array<ArrayBuffer>) => void
  // Channel-based listeners (optimized)
  addChannelListener: (
    channelKey: ChannelKey,
    callback: MessageListener,
    scopes?: readonly string[]
  ) => void
  removeChannelListener: (
    channelKey: ChannelKey,
    callback: MessageListener,
    scopes?: readonly string[]
  ) => void
  // Global listeners (for backward compatibility)
  addListener: (callback: MessageListener) => void
  removeListener: (callback: MessageListener) => void
}

const WebSocketContext = createContext<WebSocketContextType | null>(null)
const BulkWebSocketContext = createContext<WebSocketContextType | null>(null)
const CriticalWebSocketContext = createContext<WebSocketContextType | null>(null)

// Debug flag
const DEBUG_WS = false // TEMP: diagnosing double-auth toast, revert to false
const log = (...args: unknown[]) => {
  if (DEBUG_WS) console.log('[WebSocketProvider]', ...args)
}

// resubscribeAll() covers every ref-counted-active channel in the session (account
// streams, watchlist, a wide/deep option chain, ...), which can run into the
// hundreds. Sending that as one subscribe message risks tripping the server's
// rate/subscription limit — chunk it and stagger the sends instead.
const RESUBSCRIBE_CHUNK_SIZE = 40
const RESUBSCRIBE_CHUNK_DELAY_MS = 150

// Caps the message.error -> resubscribeAll() recovery loop below. Without a cap, a
// server that keeps rejecting the resubscribe (a persistent limit, not a transient
// blip) would retry forever every couple seconds.
const MAX_ERROR_RESUBSCRIBE_ATTEMPTS = 3
const ERROR_RESUBSCRIBE_BASE_DELAY_MS = 2_000
const ERROR_RESUBSCRIBE_MAX_DELAY_MS = 16_000
const ERROR_RESUBSCRIBE_RESET_WINDOW_MS = 30_000

/**
 * Extract all channel keys from ServerMessage for routing
 * Returns all matching channel identifiers based on message data types
 */
export function extractChannelKeys(message: ServerMessage): ChannelKey[] {
  const keys: ChannelKey[] = []

  const data = message.data
  if (!data) return keys

  // Market data channels
  if (data.tickers) keys.push('marketDataStream:tickers')
  if (data.incrementalTickers) keys.push('marketDataStream:incrementalTickers')
  if (data.chartData) keys.push('chartDataStream')
  if (data.orderBooks) keys.push('marketDataStream:orderbook')
  if (data.instrumentTrades) {
    keys.push('marketDataStream:trades')
    keys.push('optionsTradesStream')
  }

  // Account data channels
  if (
    data.trades ||
    data.positions ||
    data.unifiedOrders ||
    data.portfolioData ||
    data.rfqUpdates ||
    data.rfqAccept ||
    data.rfqMakerRfqs ||
    data.rfqMakerQuotes ||
    data.accountFees ||
    data.accountSummary
  ) {
    keys.push('accountStream')
  }

  // Exchange data
  if (data.instruments) keys.push('exchangeData')

  // Service notifications
  if (data.notification) keys.push('serviceNotifications')

  if (data.exchangeStatus) keys.push('exchangeStatusStream')
  if (data.venueHealth) keys.push('venueHealthStream')
  if (data.marketScreenerFrame) keys.push('marketScreenerStream')
  if (data.orderbookHeatmap && isLiveOrderbookHeatmap(data.orderbookHeatmap)) {
    keys.push('orderbookHeatmapStream')
  }
  if (data.economicCalendarEvent) keys.push('economicCalendarStream')
  if (data.tokenUnlockEvent) keys.push('tokenUnlockStream')
  if (data.triggerEvaluationSnapshot) keys.push('triggerConditionStream')
  if (data.dbNewsPost) keys.push('dbNewsStream')
  if (data.alpacaNewsPost) keys.push('alpacaNewsStream')
  if (data.twitterPost) keys.push('twitterStream')
  if (data.treeNewsPost) keys.push('treeNewsStream')
  if (data.coinmarketcalEvent) keys.push('coinmarketcalEventStream')
  if (data.executionAnalytics) keys.push('executionAnalyticsStream')
  if (data.optionsVolumeStreamUpdate) keys.push('optionsVolumeStream')
  // The server does not echo which subscription a message answers, so the channel
  // is inferred from which DataPayload arm is populated. A payload missing from
  // this list is dropped with no error anywhere — which is why it has to be
  // extended alongside every new analytics stream.
  //
  // NOTE: this list is also missing seven Amberdata arms that exist in
  // AMBERDATA_ANALYTICS_PAYLOAD_KEYS (usdGamma, gex, volCones, skewConstant,
  // skewFloating, rvSeasonalityDow, rvSeasonalityMoy), so those widgets' pushes
  // are dropped today. Left alone deliberately: adding them turns streaming ON for
  // shipped widgets, which is a behaviour change that deserves its own PR.
  if (
    data.volMetrics24h ||
    data.putCallRatio ||
    data.deltaSurfaceConstant ||
    data.deltaSurfaceFloating ||
    data.impliedVsRealized ||
    data.termStructureConstant ||
    data.termStructureFloating ||
    data.termStructureRichness ||
    data.volatilityIndex ||
    data.fedwatchProbabilities ||
    data.liquidationLevelProfile ||
    data.stopLevelProfile
  ) {
    keys.push('analyticsStream')
  }

  return keys
}

function WebSocketLaneProvider({
  children,
  context: LaneContext,
  lane,
}: {
  children: React.ReactNode
  context: React.Context<WebSocketContextType | null>
  lane: WebSocketTransportLane
}) {
  const [shouldConnect, setShouldConnect] = useState(false)

  // Subscription management: Key → { count, channel }
  const subscriptionsRef = useRef<Map<string, { count: number; channel: Partial<MessageSubject> }>>(
    new Map()
  )

  // Channel-based listeners (optimized) - only receives messages for their channel
  const channelListenersRef = useRef<ScopedListenerRegistry>(new Map())

  // Global listeners (backward compatibility) - receives all messages
  const globalListenersRef = useRef<Set<MessageListener>>(new Set())

  // Pending subscriptions queue
  const pendingSubscriptionsRef = useRef<Partial<MessageSubject>[]>([])

  // Ticker channels whose count reached 0 but stay on the wire for a grace period, so a remount
  // (page switch) takes them back without a round trip. Their subscriptionsRef entry stays, with
  // count 0, so a reconnect resubscribes them too. Expiry goes through a ref because the timer
  // outlives the render that scheduled it.
  const releaseChannelsRef = useRef<(keys: string[]) => void>(() => {})
  const [lingeringTickers] = useState(() =>
    createLingeringChannels((keys) => releaseChannelsRef.current(keys))
  )
  const [{ tickerCache, incrementalTickerCache }] = useState(() => ({
    tickerCache: createTickerChannelCache(),
    incrementalTickerCache: createIncrementalTickerCache(),
  }))
  const [heatmapCache] = useState(createHeatmapChannelCache)

  // Track if this is first connection or reconnection
  const hasConnectedOnceRef = useRef(false)

  // Debounce rate-limit toasts so we don't spam the user
  const lastRateLimitToastRef = useRef(0)

  // A server error (rate limit / subscription limit exceeded) can silently drop some
  // channels from a subscribe batch — the server doesn't say which ones, and `subscribe()`
  // already marked all of them as locally subscribed before the send, so nothing else
  // retries them. Those channels then never receive data again (e.g. option-chain rows
  // stuck on "loading" forever). Recover by re-sending every currently-active channel
  // once, debounced so a burst of errors only triggers a single resubscribe.
  const errorResubscribeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Consecutive-attempt counter + backoff for the above — without a cap, a server
  // that keeps rejecting the resubscribe (not just a transient blip) would retry
  // every couple seconds forever. Reset once ERROR_RESUBSCRIBE_RESET_WINDOW_MS
  // passes without another error, so an unrelated one-off error later doesn't
  // inherit an already-elevated backoff/count from a storm that's long over.
  const errorResubscribeAttemptsRef = useRef(0)
  const lastErrorAtRef = useRef(0)

  // Pending chunk-send timers scheduled by resubscribeAll() — tracked so a second
  // resubscribeAll() call (e.g. the rate-limit recovery path firing again before an
  // earlier run finished sending) can cancel any still-pending chunks instead of
  // interleaving two staggered sends, and so unmount can cancel them cleanly.
  const resubscribeChunkTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // Auth state tracking
  const isWsAuthenticatedRef = useRef(false)
  const isSocketReadyRef = useRef(false)
  const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Identifies the socket open that owns the current authentication attempt. `onOpen`
  // waits asynchronously for a valid token; a restart can replace that socket while the
  // await is pending. Without an attempt id, the stale continuation sends its auth frame
  // through sendProtoRef to the new socket and authenticates it twice.
  const authAttemptRef = useRef(0)
  const authAttemptBySocketRef = useRef(new WeakMap<EventTarget, number>())

  // ─── Auth-failure backoff ─────────────────────────────────────────────────
  // react-use-websocket's own backoff can't see these failures: it resets its attempt
  // counter on every `open`, and the server accepts the socket before rejecting the
  // token. The rejection path also turns shouldConnect off, so the next subscribe()
  // or connect() used to reopen at once, with no delay at all. Every reopen now goes
  // through openWhenAllowed(), which holds it until nextConnectAtRef.
  //
  // wantConnectedRef: whether the app wants this lane open (connect/restart vs
  // disconnect), separate from shouldConnect, which is off while backing off.
  const wantConnectedRef = useRef(false)
  // Consecutive sockets that closed without ever authenticating. Reset on a successful
  // auth, a new access token, the browser coming back online, or the tab becoming visible.
  const authFailureStreakRef = useRef(0)
  const nextConnectAtRef = useRef(0)
  const reopenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Tells shouldReconnect to leave a server-closed, never-authenticated socket to the
  // backoff above instead of the library's 1s retry.
  const skipLibraryReconnectRef = useRef(false)

  const clearReopenTimer = useCallback(() => {
    if (reopenTimerRef.current !== null) {
      clearTimeout(reopenTimerRef.current)
      reopenTimerRef.current = null
    }
  }, [])

  const openWhenAllowed = useCallback(() => {
    if (!wantConnectedRef.current) return
    const waitMs = nextConnectAtRef.current - Date.now()
    if (waitMs <= 0) {
      clearReopenTimer()
      setShouldConnect(true)
      return
    }
    if (reopenTimerRef.current !== null) return
    log(`Auth backoff: reopening in ${waitMs}ms`)
    reopenTimerRef.current = setTimeout(() => {
      reopenTimerRef.current = null
      if (wantConnectedRef.current) setShouldConnect(true)
    }, waitMs)
  }, [clearReopenTimer])

  const recordAuthFailure = useCallback(() => {
    authFailureStreakRef.current += 1
    nextConnectAtRef.current = Date.now() + authRetryDelayMs(authFailureStreakRef.current)
  }, [])

  /** Drop any auth backoff and, if one was holding the lane closed, reopen now. */
  const resetAuthBackoff = useCallback(() => {
    if (authFailureStreakRef.current === 0) return
    authFailureStreakRef.current = 0
    nextConnectAtRef.current = 0
    if (reopenTimerRef.current !== null) {
      clearReopenTimer()
      openWhenAllowed()
    }
  }, [clearReopenTimer, openWhenAllowed])

  // A new token is the one thing that can fix a rejected one, so it gets an immediate try.
  const { accessToken } = useAtomValue(authAtom)
  const prevAccessTokenRef = useRef(accessToken)
  useEffect(() => {
    if (prevAccessTokenRef.current === accessToken) return
    prevAccessTokenRef.current = accessToken
    resetAuthBackoff()
  }, [accessToken, resetAuthBackoff])

  // The user coming back (tab visible again) or the network returning shouldn't
  // leave them waiting out a backoff that grew while nobody was looking.
  useEffect(() => {
    const onOnline = () => resetAuthBackoff()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') resetAuthBackoff()
    }
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [resetAuthBackoff])

  // ─── Runtime health instrumentation ───────────────────────────────────────
  // Mutable, never state: these fields change on every inbound frame.
  //
  // The three message timestamps are tracked separately because the server
  // pings on a fixed cadence. A single "last message" stamp would stay fresh
  // while the data stream is completely dead, so anything asking "is data
  // flowing?" has to look at lastDataMessageAt, while "is the transport alive?"
  // looks at lastTransportMessageAt.
  const healthRef = useRef<WebSocketHealth>({
    connectionStatus: 'UNINSTANTIATED',
    isReady: false,
    lastTransportMessageAt: null,
    lastHeartbeatAt: null,
    lastDataMessageAt: null,
    lastAuthenticatedAt: null,
    activeSubscriptionCount: 0,
    reconnectCount: 0,
  })

  // Resolved when the socket next reaches the authenticated-and-ready state.
  // restart() parks a waiter here rather than polling.
  const readyWaitersRef = useRef<Set<() => void>>(new Set())
  // Every timer restart() schedules, so unmount can cancel them all.
  const restartTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  // Non-null while a restart() close-then-reopen cycle is in flight. A second
  // caller overlapping the first (e.g. the recovery controller's Level 1 and
  // the auth-transition effect firing close together) must not each run their
  // own close()+reopen — two overlapping cycles can each open a fresh socket
  // and send its own AuthenticateRequest, which is what the server's "Already
  // Authenticated" / "Authentication is needed to subscribe" errors are.
  const restartInFlightRef = useRef<Promise<RestartResult> | null>(null)

  const markSocketReady = useCallback(() => {
    isSocketReadyRef.current = true
    healthRef.current.isReady = true
    healthRef.current.lastAuthenticatedAt = Date.now()
    // An authenticated socket means the network is back: let the offline bar's loop check now.
    noteConnectionActivity()
    // Copy first: a waiter removes itself from the set as it settles.
    const waiters = Array.from(readyWaitersRef.current)
    readyWaitersRef.current.clear()
    waiters.forEach((waiter) => waiter())
  }, [])

  const markSocketNotReady = useCallback(() => {
    isSocketReadyRef.current = false
    healthRef.current.isReady = false
  }, [])

  const clearAuthTimeout = useCallback(() => {
    if (authTimeoutRef.current !== null) {
      clearTimeout(authTimeoutRef.current)
      authTimeoutRef.current = null
    }
  }, [])

  const invalidateAuthAttempt = useCallback(() => {
    authAttemptRef.current += 1
    isWsAuthenticatedRef.current = false
    markSocketNotReady()
    clearAuthTimeout()
  }, [clearAuthTimeout, markSocketNotReady])

  // Stable refs to break circular dependency between processMessage and useBaseWebSocket
  const sendProtoRef = useRef<(msg: Uint8Array<ArrayBuffer>) => void>(() => {})
  const closeRef = useRef<() => void>(() => {})
  const processMessageRef = useRef<(e: MessageEvent) => void>(() => {})

  const refreshCoordinatorRef = useRef<ReturnType<
    typeof createSubscriptionRefreshCoordinator
  > | null>(null)
  if (refreshCoordinatorRef.current === null) {
    refreshCoordinatorRef.current = createSubscriptionRefreshCoordinator({
      isActive: (channel) => {
        const subscription = subscriptionsRef.current.get(JSON.stringify(channel))
        return subscription !== undefined && subscription.count > 0
      },
      isReady: () => isSocketReadyRef.current,
      send: (channels, isSubscribe) => {
        const request = ClientMessage.create(
          isSubscribe ? { subscribe: { channels } } : { unsubscribe: { channels } }
        )
        sendProtoRef.current(new Uint8Array(ClientMessage.encode(request).finish()))
      },
    })
  }

  useEffect(() => () => refreshCoordinatorRef.current?.dispose(), [])

  useEffect(
    () => () => {
      if (errorResubscribeTimeoutRef.current !== null) {
        clearTimeout(errorResubscribeTimeoutRef.current)
      }
      resubscribeChunkTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
      resubscribeChunkTimeoutsRef.current = []
      restartTimersRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
      restartTimersRef.current.clear()
      readyWaitersRef.current.clear()
      restartInFlightRef.current = null
      authAttemptRef.current += 1
      clearAuthTimeout()
      clearReopenTimer()
    },
    [clearAuthTimeout, clearReopenTimer]
  )

  // Stream instrumentation is enabled by the manual debug flag or for the
  // production sample. The decision is cached so the per-frame path never
  // touches browser storage.
  const streamTelemetryEnabledRef = useRef(false)
  useEffect(() => {
    streamTelemetryEnabledRef.current = isStreamDebugEnabled() || isStreamPerformanceSampled()
  }, [])

  // Base WebSocket Hook
  const { sendProto, close, connectionStatus } = useBaseWebSocket({
    connected: shouldConnect,
    options: {
      onMessage: (e) => {
        // A live socket is proof the network works, even when navigator.onLine says otherwise
        // or the socket never dropped. A no-op (one atom read) while the app is online.
        noteConnectionActivity()
        processMessageRef.current(e)
      },
      // filter: false prevents react-use-websocket from calling flushSync to
      // update lastMessage state. onMessage above still fires for every message.
      filter: () => false,
      onOpen: async (event) => {
        // Browser WebSockets default binary frames to Blob, which adds an async
        // conversion and allocation to every hot-path frame. Set this before auth
        // so every subsequent server frame arrives ready for protobuf decoding.
        if (event.target && 'binaryType' in event.target) {
          ;(event.target as WebSocket).binaryType = 'arraybuffer'
        }
        const authAttempt = authAttemptRef.current + 1
        authAttemptRef.current = authAttempt
        if (event.target) authAttemptBySocketRef.current.set(event.target, authAttempt)
        clearAuthTimeout()
        isWsAuthenticatedRef.current = false
        markSocketNotReady()
        // hasConnectedOnceRef only flips after the *first* successful auth, so
        // seeing it already set here means this open is a reconnect.
        if (hasConnectedOnceRef.current) {
          healthRef.current.reconnectCount += 1
        }
        let token: string | null
        try {
          token = await serviceUtil.ensureTokenValid()
        } catch {
          if (authAttemptRef.current === authAttempt) {
            invalidateAuthAttempt()
            // No retry of its own: without a token there is nothing to retry with. The
            // next connect() (or a new token) reopens, held by the backoff.
            recordAuthFailure()
            setShouldConnect(false)
            closeRef.current()
          }
          return
        }

        // The socket that started this token lookup has already closed or been
        // replaced. Never send its auth frame through the ref for the new socket.
        if (authAttemptRef.current !== authAttempt) return

        if (!token) {
          invalidateAuthAttempt()
          recordAuthFailure()
          setShouldConnect(false)
          closeRef.current()
          return
        }

        log('Connected. Sending auth...')
        sendProtoRef.current(new Uint8Array(encodeAuthMessage(token)))

        // Auth timeout (~10s)
        authTimeoutRef.current = setTimeout(() => {
          if (authAttemptRef.current === authAttempt && !isWsAuthenticatedRef.current) {
            authTimeoutRef.current = null
            console.error('[WebSocket] Auth timeout')
            invalidateAuthAttempt()
            recordAuthFailure()
            setShouldConnect(false)
            closeRef.current()
            openWhenAllowed()
          }
        }, 10000)
      },
      onClose: (event) => {
        // Also invalidates an ensureTokenValid() continuation still pending for
        // this socket and prevents its timer from firing after the close. A late
        // close from a replaced socket must not invalidate the newer attempt.
        const closingAttempt = event.target
          ? authAttemptBySocketRef.current.get(event.target)
          : undefined
        // Opened but closed before authenticating, by the other side: every close of
        // our own (auth failure, restart, disconnect) invalidates the attempt first,
        // so it no longer matches here. Back off instead of the library's 1s retry.
        if (
          closingAttempt !== undefined &&
          closingAttempt === authAttemptRef.current &&
          !isWsAuthenticatedRef.current
        ) {
          recordAuthFailure()
          skipLibraryReconnectRef.current = true
          setShouldConnect(false)
          openWhenAllowed()
        }
        if (closingAttempt === undefined || closingAttempt === authAttemptRef.current) {
          invalidateAuthAttempt()
          // Ticks missed while disconnected would make the cached ones stale.
          tickerCache.clear()
          incrementalTickerCache.clear()
          heatmapCache.clear()
        }
      },
      shouldReconnect: () => {
        if (skipLibraryReconnectRef.current) {
          skipLibraryReconnectRef.current = false
          return false
        }
        return shouldConnect
      },
      // Keep trying indefinitely while shouldConnect is true — background tabs can
      // exhaust a fixed attempt budget before the user returns.
      reconnectAttempts: Infinity,
      // Exponential backoff: 1s → 2s → 4s → … capped at 30s
      reconnectInterval: (attempt) => {
        const delayMs = Math.min(Math.pow(2, attempt) * 1000, 30_000)
        if (streamTelemetryEnabledRef.current) {
          recordStreamPerformanceEvent({ type: 'reconnect-scheduled', delayMs })
        }
        return delayMs
      },
    },
  })

  // Keep refs in sync (stable, no re-render needed)
  sendProtoRef.current = sendProto
  closeRef.current = close
  healthRef.current.connectionStatus = connectionStatus

  const isConnected = connectionStatus === 'OPEN'

  // Market-data cadence (250 ms / 1000 ms, see stream-interval.ts). The server forgets it
  // per socket, so it goes out on every open — connect and reconnect alike — and again
  // whenever the setting changes. Allowed before or after Authenticate; no reply.
  const streamInterval = useEffectiveStreamInterval()
  useEffect(() => {
    if (!isConnected) return
    log('Setting stream interval', streamInterval)
    sendProtoRef.current(encodeSetStreamInterval(streamInterval))
  }, [isConnected, streamInterval])

  // Stable wire sender: use the latest socket function without making every subscriber
  // callback change identity whenever react-use-websocket returns a new send function.
  const sendSubscription = useCallback(
    (channels: Partial<MessageSubject>[], isSubscribe: boolean) => {
      if (channels.length === 0) return

      const request = ClientMessage.create(
        isSubscribe ? { subscribe: { channels } } : { unsubscribe: { channels } }
      )
      const binary = ClientMessage.encode(request).finish()
      sendProtoRef.current(new Uint8Array(binary))
    },
    []
  )

  // Flush pending subscriptions
  const flushPendingSubscriptions = useCallback(() => {
    if (pendingSubscriptionsRef.current.length > 0) {
      sendSubscription(pendingSubscriptionsRef.current, true)
      pendingSubscriptionsRef.current = []
    }
  }, [sendSubscription])

  // Re-subscribe all active channels on reconnection
  const resubscribeAll = useCallback(() => {
    const activeChannels: Partial<MessageSubject>[] = []
    subscriptionsRef.current.forEach((value, key) => {
      if (value.count > 0 || lingeringTickers.has(key)) {
        activeChannels.push(value.channel)
      }
    })
    if (activeChannels.length === 0) return

    // Cancel any chunks still pending from a previous run (see the ref's comment)
    // instead of letting two staggered sends interleave.
    resubscribeChunkTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
    resubscribeChunkTimeoutsRef.current = []

    log(`Resubscribing to ${activeChannels.length} channels`)
    if (streamTelemetryEnabledRef.current) {
      recordStreamPerformanceEvent({
        type: 'resubscribe-run',
        channelCount: activeChannels.length,
      })
    }

    for (let i = 0; i < activeChannels.length; i += RESUBSCRIBE_CHUNK_SIZE) {
      const chunk = activeChannels.slice(i, i + RESUBSCRIBE_CHUNK_SIZE)
      const delay = (i / RESUBSCRIBE_CHUNK_SIZE) * RESUBSCRIBE_CHUNK_DELAY_MS
      const timeoutId = setTimeout(() => sendSubscription(chunk, true), delay)
      resubscribeChunkTimeoutsRef.current.push(timeoutId)
    }
  }, [lingeringTickers, sendSubscription])

  // Connect/Disconnect
  const connect = useCallback(() => {
    log('Connecting...')
    wantConnectedRef.current = true
    openWhenAllowed()
  }, [openWhenAllowed])

  const disconnect = useCallback(() => {
    log('Disconnecting...')
    wantConnectedRef.current = false
    clearReopenTimer()
    invalidateAuthAttempt()
    setShouldConnect(false)
    close()
  }, [close, clearReopenTimer, invalidateAuthAttempt])

  /**
   * Close and reopen, resolving on re-authentication. The only place the
   * close-then-reopen sequence lives, so recovery and the auth transition can't
   * both drive the socket and end up restarting it twice per event.
   *
   * Note this reads closeRef, not `close` — the callback is intentionally
   * dependency-free so the recovery controller's effect never re-runs on a new
   * socket function identity, which means it must not close over one either.
   */
  const restart = useCallback((): Promise<RestartResult> => {
    // Join the in-flight cycle instead of starting a second one. Two
    // overlapping close()+reopen sequences would each spin up their own
    // WebSocket and send their own auth — see restartInFlightRef's comment.
    if (restartInFlightRef.current) {
      log('Restart already in flight, joining it...')
      return restartInFlightRef.current
    }

    log('Restarting...')
    invalidateAuthAttempt()
    setShouldConnect(false)
    closeRef.current()

    const promise = new Promise<RestartResult>((resolve) => {
      let settled = false

      const settle = (result: RestartResult) => {
        if (settled) return
        settled = true
        readyWaitersRef.current.delete(onReady)
        clearTimeout(timeoutId)
        restartTimersRef.current.delete(timeoutId)
        if (restartInFlightRef.current === promise) restartInFlightRef.current = null
        resolve(result)
      }

      const onReady = () => settle('ready')
      readyWaitersRef.current.add(onReady)

      const timeoutId = setTimeout(() => settle('timeout'), RESTART_TIMEOUT_MS)
      restartTimersRef.current.add(timeoutId)

      // Small delay so the close propagates before we ask to reopen; also
      // resets react-use-websocket's backoff counter. Still held by the auth
      // backoff, so a recovery restart can't hammer a rejected token either; a
      // new token (the auth-transition restart) has already cleared it by now.
      const reconnectId = setTimeout(() => {
        restartTimersRef.current.delete(reconnectId)
        wantConnectedRef.current = true
        openWhenAllowed()
      }, RESTART_RECONNECT_DELAY_MS)
      restartTimersRef.current.add(reconnectId)
    })

    restartInFlightRef.current = promise
    return promise
  }, [invalidateAuthAttempt, openWhenAllowed])

  const getHealth = useCallback(
    (): WebSocketHealth => ({
      ...healthRef.current,
      // Counted at read time rather than mirrored on every subscribe/unsubscribe. Lingering
      // ticker channels (count 0) have no subscriber, so they don't count.
      activeSubscriptionCount: Array.from(subscriptionsRef.current.values()).filter(
        (entry) => entry.count > 0
      ).length,
    }),
    []
  )
  const getListenerCount = useCallback(
    () => globalListenersRef.current.size + countScopedListeners(channelListenersRef.current),
    []
  )
  const getCachedIncrementalTicker = incrementalTickerCache.get
  const getCachedTicker = tickerCache.get
  const getCachedHeatmapColumn = heatmapCache.get
  const getActiveChannels = useCallback(
    () =>
      Array.from(subscriptionsRef.current.entries())
        .filter(([, value]) => value.count > 0)
        .map(([key]) => key),
    []
  )

  // Subscribe to channels
  const subscribe = useCallback(
    (channels: Partial<MessageSubject>[]) => {
      const channelsToSubscribe: Partial<MessageSubject>[] = []

      channels.forEach((channel) => {
        const key = JSON.stringify(channel)
        const current = subscriptionsRef.current.get(key)

        if (!current) {
          subscriptionsRef.current.set(key, { count: 1, channel })
          // Whatever the cache holds for this symbol predates the subscription; the server's
          // snapshot replaces it.
          tickerCache.forget(channel)
          incrementalTickerCache.forget(channel)
          heatmapCache.forget(channel)
          channelsToSubscribe.push(channel)
          log('Subscribe (new):', key)
        } else if (current.count <= 0) {
          // A lingering ticker channel: still on the wire, so take it back without a send.
          current.count = 1
          lingeringTickers.take(key)
          log('Subscribe (reused lingering):', key)
        } else {
          current.count += 1
          log('Subscribe (ref count++):', key, 'count:', current.count)
        }
      })

      if (channelsToSubscribe.length > 0) {
        if (!shouldConnect) {
          log('Auto-connecting on first subscribe...')
          pendingSubscriptionsRef.current.push(...channelsToSubscribe)
          connect()
        } else if (isConnected && isWsAuthenticatedRef.current) {
          log('Sending subscribe for', channelsToSubscribe.length, 'channels')
          sendSubscription(channelsToSubscribe, true)
        } else {
          log('Queueing subscribe (not connected/authenticated yet)')
          pendingSubscriptionsRef.current.push(...channelsToSubscribe)
        }
      }
    },
    [
      shouldConnect,
      isConnected,
      sendSubscription,
      lingeringTickers,
      tickerCache,
      incrementalTickerCache,
      heatmapCache,
      connect,
    ]
  )

  // Drop channels whose ref count is 0, from the queue and the wire.
  const releaseChannels = useCallback(
    (keys: string[]) => {
      const channelsToUnsubscribe: Partial<MessageSubject>[] = []

      keys.forEach((key) => {
        const current = subscriptionsRef.current.get(key)
        if (!current || current.count > 0) return
        lingeringTickers.take(key)
        subscriptionsRef.current.delete(key)
        tickerCache.forget(current.channel)
        incrementalTickerCache.forget(current.channel)
        heatmapCache.forget(current.channel)
        channelsToUnsubscribe.push(current.channel)
        log('Unsubscribe (removed):', key)
      })

      if (channelsToUnsubscribe.length === 0) return

      // A channel queued in pendingSubscriptionsRef while the socket was still
      // connecting/authenticating (e.g. a watchlist window scrolled past it before
      // auth finished) never gets pruned by the wire-level unsubscribe below, since
      // that only fires when isConnected. Left in place, every symbol ever
      // subscribed-then-unsubscribed pre-auth accumulates here and gets sent as one
      // giant burst on flushPendingSubscriptions() — easily blowing past the
      // server's per-connection subscription limit on a large, fast-scrolled list.
      const removedKeys = new Set(channelsToUnsubscribe.map((channel) => JSON.stringify(channel)))
      pendingSubscriptionsRef.current = pendingSubscriptionsRef.current.filter(
        (channel) => !removedKeys.has(JSON.stringify(channel))
      )

      if (isConnected) {
        log('Sending unsubscribe for', channelsToUnsubscribe.length, 'channels')
        sendSubscription(channelsToUnsubscribe, false)
      }
    },
    [
      isConnected,
      sendSubscription,
      lingeringTickers,
      tickerCache,
      incrementalTickerCache,
      heatmapCache,
    ]
  )
  releaseChannelsRef.current = releaseChannels

  useEffect(() => () => lingeringTickers.dispose(), [lingeringTickers])

  // Unsubscribe from channels
  const unsubscribe = useCallback(
    (channels: Partial<MessageSubject>[]) => {
      const toRelease: string[] = []
      const toLinger: string[] = []

      channels.forEach((channel) => {
        const key = JSON.stringify(channel)
        const current = subscriptionsRef.current.get(key)

        if (!current || current.count <= 0) {
          log('Unsubscribe (not found):', key)
          return
        }
        current.count -= 1
        log('Unsubscribe (ref count--):', key, 'count:', current.count)
        if (current.count > 0) return
        // Linger only on a live socket: before auth the channel is merely queued, and pruning
        // the queue right away is what keeps a fast pre-auth scroll from bursting it.
        if (shouldLingerTickerChannel(channel, isConnected, isWsAuthenticatedRef.current)) {
          toLinger.push(key)
        } else toRelease.push(key)
      })

      releaseChannels([...toRelease, ...lingeringTickers.hold(toLinger)])
    },
    [isConnected, releaseChannels, lingeringTickers]
  )

  // Force a server-side channel refresh while preserving local subscription ref-counts.
  // The returned count lets callers distinguish "refresh sent" from "coordinator cooldown
  // swallowed it", so a retry loop doesn't count a request that never reached the wire.
  const refreshSubscriptions = useCallback((channels: Partial<MessageSubject>[]): number => {
    return refreshCoordinatorRef.current?.refresh(channels) ?? 0
  }, [])

  // Channel-based listener management (optimized)
  const addChannelListener = useCallback(
    (channelKey: ChannelKey, callback: MessageListener, scopes?: readonly string[]) => {
      addScopedListener(channelListenersRef.current, channelKey, callback, scopes)
    },
    []
  )

  const removeChannelListener = useCallback(
    (channelKey: ChannelKey, callback: MessageListener, scopes?: readonly string[]) => {
      removeScopedListener(channelListenersRef.current, channelKey, callback, scopes)
    },
    []
  )

  // Global listener management (backward compatibility)
  const addListener = useCallback((callback: MessageListener) => {
    globalListenersRef.current.add(callback)
  }, [])

  const removeListener = useCallback((callback: MessageListener) => {
    globalListenersRef.current.delete(callback)
  }, [])

  // Message processing with channel-based routing.
  // Uses sendProtoRef/closeRef to avoid capturing hook return values as deps,
  // which would force re-creation and break the onMessage option reference.
  const processMessage = useCallback(
    async (messageEvent: MessageEvent) => {
      try {
        const telemetryOn = streamTelemetryEnabledRef.current
        const queueLagMs = telemetryOn ? messageEventQueueLagMs(messageEvent.timeStamp) : null
        let arrayBuffer: ArrayBuffer

        if (messageEvent.data instanceof ArrayBuffer) {
          arrayBuffer = messageEvent.data
        } else if (messageEvent.data instanceof Blob) {
          arrayBuffer = await messageEvent.data.arrayBuffer()
        } else {
          return
        }

        const t0 = telemetryOn ? performance.now() : 0
        const message = decodeServerMessage(new Uint8Array(arrayBuffer))
        const t1 = telemetryOn ? performance.now() : 0
        const messageAttempt = messageEvent.target
          ? authAttemptBySocketRef.current.get(messageEvent.target)
          : undefined
        if (messageAttempt !== undefined && messageAttempt !== authAttemptRef.current) return
        healthRef.current.lastTransportMessageAt = Date.now()

        // Handle Ping/Pong internally
        if (message.ping) {
          healthRef.current.lastHeartbeatAt = Date.now()
          sendProtoRef.current(new Uint8Array(encodePong(message.ping.timestamp)))
          return
        }

        // Handle AuthenticateResponse
        if (message.authenticateResponse) {
          clearAuthTimeout()

          if (message.authenticateResponse.success) {
            isWsAuthenticatedRef.current = true
            authFailureStreakRef.current = 0
            nextConnectAtRef.current = 0
            // Also stamps lastAuthenticatedAt and releases any restart() waiter.
            markSocketReady()
            if (hasConnectedOnceRef.current) {
              log('Reconnected + authenticated. Resubscribing...')
              resubscribeAll()
            } else {
              hasConnectedOnceRef.current = true
            }
            flushPendingSubscriptions()
          } else {
            log('Auth failed:', message.authenticateResponse.errorMessage)
            invalidateAuthAttempt()
            recordAuthFailure()
            setShouldConnect(false)
            closeRef.current()
            openWhenAllowed()
          }
          return
        }

        // Handle server-side error messages (e.g. rate limit / subscription limit exceeded)
        if (message.error) {
          if (telemetryOn) recordStreamPerformanceEvent({ type: 'server-error' })
          console.warn('[WebSocket] Server error:', message.error.message)
          const now = Date.now()
          if (now - lastRateLimitToastRef.current > 10_000) {
            lastRateLimitToastRef.current = now
            toast.warning(message.error.message || 'WebSocket request was rate limited')
          }

          // A fresh storm (no error in a while) starts the attempt count over, so an
          // isolated error long after a past storm doesn't inherit its backoff.
          if (now - lastErrorAtRef.current > ERROR_RESUBSCRIBE_RESET_WINDOW_MS) {
            errorResubscribeAttemptsRef.current = 0
          }
          lastErrorAtRef.current = now

          if (errorResubscribeTimeoutRef.current !== null) {
            clearTimeout(errorResubscribeTimeoutRef.current)
            errorResubscribeTimeoutRef.current = null
          }

          if (errorResubscribeAttemptsRef.current >= MAX_ERROR_RESUBSCRIBE_ATTEMPTS) {
            console.error(
              '[WebSocket] giving up on automatic resubscribe after repeated server errors — some channels may be stuck until the next natural reconnect or a page refresh'
            )
            return
          }

          errorResubscribeAttemptsRef.current += 1
          const delay = Math.min(
            ERROR_RESUBSCRIBE_BASE_DELAY_MS * 2 ** (errorResubscribeAttemptsRef.current - 1),
            ERROR_RESUBSCRIBE_MAX_DELAY_MS
          )
          if (telemetryOn) {
            recordStreamPerformanceEvent({ type: 'resubscribe-scheduled', delayMs: delay })
          }

          // The rejected channels aren't identified in the error, so recovery re-sends
          // every active channel rather than trying to guess which ones were dropped.
          // Delayed + debounced: give the server a moment to clear whatever limit it
          // hit, and collapse a burst of errors (one per rejected channel) into one
          // resubscribe.
          errorResubscribeTimeoutRef.current = setTimeout(() => {
            errorResubscribeTimeoutRef.current = null
            resubscribeAll()
          }, delay)
          return
        }

        // Past ping/auth/error: this is real data, so it's what "is the stream
        // actually flowing?" should be measured against.
        healthRef.current.lastDataMessageAt = Date.now()

        // Recorded whether or not anyone listens, so a lingering ticker channel stays current.
        incrementalTickerCache.record(message.data?.incrementalTickers)
        if (message.data?.tickers) tickerCache.record(message.data.tickers)
        heatmapCache.record(message.data?.orderbookHeatmap)

        // t2 brackets dispatch precisely at the dispatch work itself (routing
        // + the two listener loops below) rather than from t1, which would
        // also fold in the auth/error branch checks and health-ref writes
        // above.
        const t2 = telemetryOn ? performance.now() : 0

        // Route to channel-specific listeners (optimized path)
        const channelKeys = extractChannelKeys(message)
        // Each listener is isolated: one throwing must not skip the rest of the fan-out, nor
        // surface as a decode error from the outer catch.
        channelKeys.forEach((channelKey) => {
          collectScopedListeners(channelListenersRef.current, channelKey, message).forEach(
            (listener) => {
              try {
                listener(message)
              } catch (error) {
                console.error(`[WebSocketProvider] Listener error on ${channelKey}:`, error)
              }
            }
          )
        })

        // Also notify global listeners (backward compatibility)
        globalListenersRef.current.forEach((listener) => {
          try {
            listener(message)
          } catch (error) {
            console.error('[WebSocketProvider] Global listener error:', error)
          }
        })

        if (telemetryOn) {
          recordFrame(channelKeys, arrayBuffer.byteLength, t1 - t0, performance.now() - t2, {
            queueLagMs,
            sourceAgeMs: sourceAgeMs(message),
            transportLane: lane,
          })
        }
      } catch (error) {
        console.error('[WebSocketProvider] Message decode error:', error)
      }
    },
    [
      flushPendingSubscriptions,
      resubscribeAll,
      markSocketReady,
      clearAuthTimeout,
      invalidateAuthAttempt,
      recordAuthFailure,
      openWhenAllowed,
      lane,
      tickerCache,
      incrementalTickerCache,
      heatmapCache,
    ]
  )

  // Keep processMessageRef current so the stable onMessage closure always
  // calls the latest version (including updated resubscribeAll / flushPending closures)
  processMessageRef.current = processMessage

  const value = useMemo(
    () => ({
      isConnected,
      connectionStatus,
      connect,
      disconnect,
      restart,
      getHealth,
      getListenerCount,
      getActiveChannels,
      subscribe,
      unsubscribe,
      refreshSubscriptions,
      getCachedTicker,
      getCachedIncrementalTicker,
      getCachedHeatmapColumn,
      sendProto,
      addChannelListener,
      removeChannelListener,
      addListener,
      removeListener,
    }),
    [
      isConnected,
      connectionStatus,
      connect,
      disconnect,
      restart,
      getHealth,
      getListenerCount,
      getActiveChannels,
      subscribe,
      unsubscribe,
      refreshSubscriptions,
      getCachedTicker,
      getCachedIncrementalTicker,
      getCachedHeatmapColumn,
      sendProto,
      addChannelListener,
      removeChannelListener,
      addListener,
      removeListener,
    ]
  )

  return <LaneContext.Provider value={value}>{children}</LaneContext.Provider>
}

function requireLaneContext(
  context: WebSocketContextType | null,
  lane: WebSocketTransportLane
): WebSocketContextType {
  if (!context) throw new Error(`${lane} WebSocket lane is unavailable`)
  return context
}

function latestTimestamp(left: number | null, right: number | null): number | null {
  if (left === null) return right
  if (right === null) return left
  return Math.max(left, right)
}

function earliestTimestamp(left: number | null, right: number | null): number | null {
  if (left === null) return right
  if (right === null) return left
  return Math.min(left, right)
}

function RoutedWebSocketProvider({ children }: { children: React.ReactNode }) {
  const bulk = requireLaneContext(useContext(BulkWebSocketContext), 'bulk')
  const critical = requireLaneContext(useContext(CriticalWebSocketContext), 'critical')
  const bulkGetListenerCount = bulk.getListenerCount
  const criticalGetListenerCount = critical.getListenerCount
  const bulkAddChannelListener = bulk.addChannelListener
  const criticalAddChannelListener = critical.addChannelListener
  const bulkRemoveChannelListener = bulk.removeChannelListener
  const criticalRemoveChannelListener = critical.removeChannelListener
  const bulkAddListener = bulk.addListener
  const criticalAddListener = critical.addListener
  const bulkRemoveListener = bulk.removeListener
  const criticalRemoveListener = critical.removeListener
  const bulkConnect = bulk.connect
  const criticalConnect = critical.connect
  const bulkDisconnect = bulk.disconnect
  const criticalDisconnect = critical.disconnect

  // Open both lanes as soon as a logged-in session mounts, instead of waiting
  // for the first widget to subscribe. The socket open + auth round trips then
  // overlap page bootstrap rather than sitting between widget mount and first data.
  const { isAuthenticated, accessToken } = useAtomValue(authAtom)
  const isLoggedIn = isAuthenticated && Boolean(accessToken)
  const wasLoggedInRef = useRef(isLoggedIn)
  useEffect(() => {
    if (isLoggedIn) {
      bulkConnect()
      criticalConnect()
    } else if (wasLoggedInRef.current) {
      bulkDisconnect()
      criticalDisconnect()
    }
    wasLoggedInRef.current = isLoggedIn
  }, [isLoggedIn, bulkConnect, criticalConnect, bulkDisconnect, criticalDisconnect])

  useEffect(() => {
    const enabled = isStreamDebugEnabled() || isStreamPerformanceSampled()
    return startSessionSampler(
      enabled,
      () => bulkGetListenerCount() + criticalGetListenerCount(),
      undefined,
      undefined,
      reportStreamPerformanceSample
    )
  }, [bulkGetListenerCount, criticalGetListenerCount])

  const subscribe = useCallback(
    (channels: Partial<MessageSubject>[]) => {
      // Authentication may not have been available when the providers first
      // mounted (for example on the login route). Any later subscription wakes
      // both lanes so the app never falls back to a single active transport.
      bulk.connect()
      critical.connect()
      const partitioned = partitionChannelsByTransport(channels)
      if (partitioned.bulk.length > 0) bulk.subscribe(partitioned.bulk)
      if (partitioned.critical.length > 0) critical.subscribe(partitioned.critical)
    },
    [bulk, critical]
  )
  const unsubscribe = useCallback(
    (channels: Partial<MessageSubject>[]) => {
      const partitioned = partitionChannelsByTransport(channels)
      if (partitioned.bulk.length > 0) bulk.unsubscribe(partitioned.bulk)
      if (partitioned.critical.length > 0) critical.unsubscribe(partitioned.critical)
    },
    [bulk, critical]
  )
  const refreshSubscriptions = useCallback(
    (channels: Partial<MessageSubject>[]) => {
      const partitioned = partitionChannelsByTransport(channels)
      return (
        bulk.refreshSubscriptions(partitioned.bulk) +
        critical.refreshSubscriptions(partitioned.critical)
      )
    },
    [bulk, critical]
  )
  const getCachedIncrementalTicker = bulk.getCachedIncrementalTicker
  const getCachedHeatmapColumn = bulk.getCachedHeatmapColumn
  const bulkGetCachedTicker = bulk.getCachedTicker
  const criticalGetCachedTicker = critical.getCachedTicker
  const getCachedTicker = useCallback(
    (exchange: unknown, symbol: string) =>
      bulkGetCachedTicker(exchange, symbol) ?? criticalGetCachedTicker(exchange, symbol),
    [bulkGetCachedTicker, criticalGetCachedTicker]
  )
  const addChannelListener = useCallback(
    (channelKey: ChannelKey, callback: MessageListener, scopes?: readonly string[]) => {
      const target =
        transportLaneForListener(channelKey) === 'critical'
          ? criticalAddChannelListener
          : bulkAddChannelListener
      target(channelKey, callback, scopes)
    },
    [bulkAddChannelListener, criticalAddChannelListener]
  )
  const removeChannelListener = useCallback(
    (channelKey: ChannelKey, callback: MessageListener, scopes?: readonly string[]) => {
      const target =
        transportLaneForListener(channelKey) === 'critical'
          ? criticalRemoveChannelListener
          : bulkRemoveChannelListener
      target(channelKey, callback, scopes)
    },
    [bulkRemoveChannelListener, criticalRemoveChannelListener]
  )
  const addListener = useCallback(
    (listener: MessageListener) => {
      bulkAddListener(listener)
      criticalAddListener(listener)
    },
    [bulkAddListener, criticalAddListener]
  )
  const removeListener = useCallback(
    (listener: MessageListener) => {
      bulkRemoveListener(listener)
      criticalRemoveListener(listener)
    },
    [bulkRemoveListener, criticalRemoveListener]
  )
  const connect = useCallback(() => {
    bulk.connect()
    critical.connect()
  }, [bulk, critical])
  const disconnect = useCallback(() => {
    bulk.disconnect()
    critical.disconnect()
  }, [bulk, critical])
  const restart = useCallback(async (): Promise<RestartResult> => {
    const results = await Promise.all([bulk.restart(), critical.restart()])
    return results.every((result) => result === 'ready') ? 'ready' : 'timeout'
  }, [bulk, critical])
  const getHealth = useCallback((): WebSocketHealth => {
    const bulkHealth = bulk.getHealth()
    const criticalHealth = critical.getHealth()

    return {
      connectionStatus: combinedWebSocketConnectionStatus(
        bulkHealth.connectionStatus,
        criticalHealth.connectionStatus
      ),
      isReady: bulkHealth.isReady && criticalHealth.isReady,
      lastTransportMessageAt: latestTimestamp(
        bulkHealth.lastTransportMessageAt,
        criticalHealth.lastTransportMessageAt
      ),
      lastHeartbeatAt: latestTimestamp(bulkHealth.lastHeartbeatAt, criticalHealth.lastHeartbeatAt),
      lastDataMessageAt: latestTimestamp(
        bulkHealth.lastDataMessageAt,
        criticalHealth.lastDataMessageAt
      ),
      lastAuthenticatedAt: earliestTimestamp(
        bulkHealth.lastAuthenticatedAt,
        criticalHealth.lastAuthenticatedAt
      ),
      activeSubscriptionCount:
        bulkHealth.activeSubscriptionCount + criticalHealth.activeSubscriptionCount,
      reconnectCount: bulkHealth.reconnectCount + criticalHealth.reconnectCount,
    }
  }, [bulk, critical])
  const getListenerCount = useCallback(
    () => bulkGetListenerCount() + criticalGetListenerCount(),
    [bulkGetListenerCount, criticalGetListenerCount]
  )
  const bulkGetActiveChannels = bulk.getActiveChannels
  const criticalGetActiveChannels = critical.getActiveChannels
  const getActiveChannels = useCallback(
    () => [...bulkGetActiveChannels(), ...criticalGetActiveChannels()],
    [bulkGetActiveChannels, criticalGetActiveChannels]
  )

  const isConnected = bulk.isConnected && critical.isConnected
  const connectionStatus = combinedWebSocketConnectionStatus(
    bulk.connectionStatus,
    critical.connectionStatus
  )

  const value = useMemo<WebSocketContextType>(
    () => ({
      isConnected,
      connectionStatus,
      connect,
      disconnect,
      restart,
      getHealth,
      getListenerCount,
      getActiveChannels,
      subscribe,
      unsubscribe,
      refreshSubscriptions,
      getCachedTicker,
      getCachedIncrementalTicker,
      getCachedHeatmapColumn,
      sendProto: bulk.sendProto,
      addChannelListener,
      removeChannelListener,
      addListener,
      removeListener,
    }),
    [
      addChannelListener,
      addListener,
      bulk,
      connect,
      connectionStatus,
      disconnect,
      getHealth,
      getListenerCount,
      getActiveChannels,
      getCachedTicker,
      getCachedIncrementalTicker,
      getCachedHeatmapColumn,
      isConnected,
      refreshSubscriptions,
      removeChannelListener,
      removeListener,
      restart,
      subscribe,
      unsubscribe,
    ]
  )

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  return (
    <WebSocketLaneProvider context={BulkWebSocketContext} lane="bulk">
      <WebSocketLaneProvider context={CriticalWebSocketContext} lane="critical">
        <RoutedWebSocketProvider>{children}</RoutedWebSocketProvider>
      </WebSocketLaneProvider>
    </WebSocketLaneProvider>
  )
}

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider')
  }
  return context
}
